import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { ElfBaseStore } from '@/store/base-store.repository';
import { DacTransactionalFormAssignmentRepositoryFactory } from '@/store/dac-state/dac-transactional-form-assignment-state/dac-transactional-form-assignment-state.repository';
import { DacTransactionalFormAssignmentStateProps } from '@/store/dac-state/dac-transactional-form-assignment-state/dac-transactional-form-assignment-state.store';
import { AttachedForm, AttachedFormDto } from '@/types';
import { form } from '@/utils/form.utils';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { select } from '@ngneat/elf';
import _ from 'lodash';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DacTransactionalFormAssignmentService implements OnDestroy {
  multiFormAccordionService = inject(MultiFormAccordionService);

  constructor(multiFormAccordionService?: MultiFormAccordionService) {
    if (multiFormAccordionService) this.multiFormAccordionService = multiFormAccordionService;
  }

  messageService = inject(MessageService);

  private storeSubscriptions: Subscription[] = [];

  initialize() {
    this.subscribeToTransactionalFormStores();
    this.storeSubscriptions.push(
      this.multiFormAccordionService.newStore$.subscribe(store => {
        this.subscribeToStore(store);
      })
    );
    this.subscribeToStores();
  }

  subscribeToTransactionalFormStores() {
    const stores = this.multiFormAccordionService.getAllStores();
    this.storeSubscriptions = stores.map(store => {
      return store.pipe(select(state => state)).subscribe();
    });
  }

  subscribeToStore(store?: ElfBaseStore) {
    const sub = store?.pipe(select(state => state)).subscribe();
    this.storeSubscriptions.push(sub!);
  }

  subscribeToStores() {
    this.subscribeToTransactionalFormStores();
    this.storeSubscriptions.push(
      this.multiFormAccordionService.newStore$.subscribe(store => {
        this.subscribeToStore(store);
      })
    );
    this.storeSubscriptions.push(this.multiFormAccordionService.storeRemoved$.subscribe());
  }

  getAllTransactionalFormsDtos() {
    return this.multiFormAccordionService.getAllStores().map((store: ElfBaseStore<DacTransactionalFormAssignmentStateProps>) => {
      const repo = DacTransactionalFormAssignmentRepositoryFactory.create(store);
      return repo.get<AttachedFormDto>('dto');
    });
  }

  validateTransactionalFormDtos(): boolean {
    let isValid = true;

    this.multiFormAccordionService.getAllStores().forEach((store: ElfBaseStore<DacTransactionalFormAssignmentStateProps>, index) => {
      const repo = DacTransactionalFormAssignmentRepositoryFactory.create(store);
      // const dto = repo.get<DynamicFieldDto>('dto');

      const result = { success: true, error: {} as any };
      if (!result.success) {
        isValid = false;
        const fieldErrors = result.success ? {} : result.error.flatten().fieldErrors;
        repo.set('errors', fieldErrors);
        MultiFormAccordionService.openPanelIfClosed(`mfa-${index}`);
        form.scrollToInvalidElement();
      } else {
        repo.set('errors', {});
      }
    });

    return isValid;
  }

  setTransactionalFormDto(response: AttachedForm): void {
    const newStore = this.multiFormAccordionService._formFactory?.(response?.id.toString());
    if (!newStore) {
      console.error(`Could not create store for dynamic field with id: ${response.id}`);
      return;
    }

    const root = this.multiFormAccordionService.root;
    if ('children' in root) {
      root.children.push({ store: newStore });
    }

    this.multiFormAccordionService.newStore$.next(newStore);

    const repo = DacTransactionalFormAssignmentRepositoryFactory.create(newStore);
    const data = {
      dynaFormId: response.id,
      dynaFormVer: response.version,
      dynaFormFieldsId: response.fields.filter(field => field.display).map(field => field.dynaFormFieldsId),
      display: true,
    } satisfies AttachedFormDto;
    repo.set('dto', data);
    repo.set('initialDto', data);
    repo.set('folderTypeSelected', { name: response.typeDossier.libelle, code: response.typeDossier.id });
    repo.set('formSelected', { name: response.name, code: `${response.id}-${response.version}` });
  }

  setTransactionalFormDtos(responses: AttachedForm[]): void {
    responses.forEach(r => {
      this.setTransactionalFormDto(r);
    });
  }

  altered(): boolean {
    return this.multiFormAccordionService.getAllStores().some(store => {
      const repo = DacTransactionalFormAssignmentRepositoryFactory.create(store);
      const current = repo.get<AttachedFormDto>('dto');
      const initial = repo.get<AttachedFormDto>('initialDto');

      if (!initial) return true;

      return !_.isEqual(current, initial);
    });
  }

  clearAttribute(attribute: string) {
    this.multiFormAccordionService.getAllStores().forEach(store => {
      const repo = DacTransactionalFormAssignmentRepositoryFactory.create(store);
      repo.setNested(attribute, undefined);
    });
  }

  clearNestedDtoAttribute(attribute: string) {
    this.multiFormAccordionService.getAllStores().forEach(store => {
      const repo = DacTransactionalFormAssignmentRepositoryFactory.create(store);
      repo.setNested('dto', attribute, undefined);
    });
  }

  resetAllDynamicFields() {
    this.multiFormAccordionService.getAllStores().forEach(store => {
      const repo = DacTransactionalFormAssignmentRepositoryFactory.create(store);
      repo.reset();
    });
  }

  ngOnDestroy() {
    this.storeSubscriptions.forEach(sub => sub.unsubscribe());
  }
}
